import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  Drawer,
  EmptyState,
  FileUpload,
  Input,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../../ui/components';
import { IconButton } from '../../../ui/components/Button';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { useMaxUploadBytes } from '../../../app/PropertyContext';
import { suppliersApi, DOCUMENT_TYPES, documentTypeLabel } from '../suppliersApi';
import { formatBytes, formatDate, daysUntil } from './utils';

function ExpiryBadge({ value }) {
  const t = useT();
  if (!value) return null;
  const days = daysUntil(value);
  if (days == null) return null;
  if (days < 0) return <Badge tone="danger" dot>{t('Expired {date}', { date: formatDate(value) })}</Badge>;
  if (days <= 30) return <Badge tone="warning" dot>{t('Expires in {days}d', { days })}</Badge>;
  return <Badge tone="neutral">{t('Expires {date}', { date: formatDate(value) })}</Badge>;
}

export default function DocumentsTab({ supplierId, onChanged }) {
  const { maxMb } = useMaxUploadBytes();
  const toast = useToast();
  const t = useT();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [docType, setDocType] = useState('Other');
  const [description, setDescription] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDocs(await suppliersApi.listDocuments(supplierId));
    } catch (err) {
      toast.error(t('Could not load documents'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [supplierId, toast, t]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setFiles([]); setDocType('Other'); setDescription(''); setExpiresAt(''); };
  const close = () => { if (!uploading) { setDrawerOpen(false); resetForm(); } };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) { toast.error(t('Choose a file to upload')); return; }
    setUploading(true);
    try {
      await suppliersApi.uploadDocument(supplierId, files[0], {
        documentType: docType,
        description: description.trim() || null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      toast.success(t('Document uploaded'));
      setDrawerOpen(false);
      resetForm();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Upload failed'), { description: err?.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await suppliersApi.openDocument(supplierId, doc.id);
    } catch (err) {
      toast.error(t('Could not open document'), { description: err?.message });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await suppliersApi.removeDocument(supplierId, deleteTarget.id);
      toast.success(t('Document removed'));
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not remove document'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="sp-panel">
      <div className="sp-section-head">
        <h2 className="sp-section-head__title">{t('Documents')}</h2>
        <Button variant="primary" size="sm" leadingIcon={<Icon.Upload size={15} />} onClick={() => setDrawerOpen(true)}>
          {t('Upload document')}
        </Button>
      </div>

      {loading ? (
        <div className="sp-center"><Spinner size={24} /></div>
      ) : docs.length === 0 ? (
        <EmptyState
          icon={<Icon.FileText size={28} />}
          title={t('No documents yet')}
          description={t('Upload contracts, insurance certificates, tax forms and price lists using the button above.')}
        />
      ) : (
        <div className="sp-list">
          {docs.map((d) => (
            <div key={d.id} className="sp-item">
              <div className="sp-item__main">
                <span className="sp-item__title">
                  <Icon.FileText size={15} /> {d.fileName}
                  <Badge tone="neutral">{t(documentTypeLabel(d.documentType))}</Badge>
                  <ExpiryBadge value={d.expiresAt} />
                </span>
                <span className="sp-item__meta">
                  <span>{formatBytes(d.sizeBytes)}</span>
                  <span>{t('Uploaded {date}', { date: formatDate(d.createdAt) })}</span>
                  {d.description && <span>· {d.description}</span>}
                </span>
              </div>
              <div className="sp-item__actions">
                <IconButton aria-label={t('Download')} variant="ghost" size="icon-sm" onClick={() => handleDownload(d)}>
                  <Icon.Download size={15} />
                </IconButton>
                <IconButton aria-label={t('Delete document')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(d)}>
                  <Icon.Trash size={15} />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer open={drawerOpen} onClose={close} title={t('Upload document')} size="md">
        <form className="sp-form" onSubmit={handleUpload}>
          <FileUpload
            files={files}
            onChange={setFiles}
            multiple={false}
            maxSizeMB={maxMb}
            hint={t('PDF, image or Office file up to {n} MB', { n: maxMb })}
          />
          <div className="ps-field">
            <span className="sp-field-label">{t('Document type')}</span>
            <Select value={docType} onChange={setDocType} options={DOCUMENT_TYPES.map((o) => ({ ...o, label: t(o.label) }))} ariaLabel={t('Document type')} />
          </div>
          <Input label={t('Expiry date (optional)')} type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          <Textarea label={t('Description (optional)')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} autoGrow maxRows={5} />
          <div className="sp-form__actions">
            <Button type="button" variant="ghost" onClick={close} disabled={uploading}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={uploading} disabled={files.length === 0}>{t('Upload')}</Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={handleDelete}
        title={t('Delete document')}
        description={deleteTarget ? t('Delete "{name}"? This permanently removes the file.', { name: deleteTarget.fileName }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
      />
    </div>
  );
}
